import { useMemo, useRef, useState } from "react";
import type { LiquidityPosition } from "@greenbook/client";
import { usePriceHistory } from "../lib/queries.js";
import { fmtCents } from "../lib/format.js";
import { useIsMobile } from "../lib/responsive.js";
import { Loading, ErrorState, EmptyState } from "./ui.js";
import { LockedInline } from "./Gate.js";
import { SCOPES, useAccess } from "../lib/omauth.js";

// Round-2 (live 2026-07-15): the server honors bucketed resolutions + `limit`, so
// each window requests an appropriate bucket size instead of raw ticks.
const RANGES = [
  { key: "1h", ms: 60 * 60 * 1000, resolution: "1m" },
  { key: "6h", ms: 6 * 60 * 60 * 1000, resolution: "5m" },
  { key: "24h", ms: 24 * 60 * 60 * 1000, resolution: "15m" },
] as const;

const POINT_LIMIT = 1500;

/**
 * Side-color pair: complementary sides of a book get distinct, CVD-safe hues
 * (kept off the green/red flash + amber-accent semantics). 3-way books add a
 * third; beyond that folds to a neutral.
 */
const SIDE_COLORS = ["#4c9ffe", "#ff9d5c", "#9085e9", "#37d67a"] as const;
const SIDE_FOLD = "#8b93a7";

/**
 * Price history for the whole book slice — ONE line per side (best available
 * price over time), not per venue. Range chips set the window. A crosshair reads
 * out every side at a moment; each line is direct-labelled at its right edge so
 * identity never rests on color alone.
 */
export function PriceHistoryPanel({
  contestId,
  positions,
  flush = false,
}: {
  contestId: string;
  /** The active book slice (both/all sides of one book). */
  positions: LiquidityPosition[];
  flush?: boolean;
}) {
  const [rangeKey, setRangeKey] = useState<(typeof RANGES)[number]["key"]>("6h");
  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[1];

  // One line per side: sort sides deterministically, fetch all hashes at once.
  const sides = useMemo(() => orderSides(positions), [positions]);
  const joinedHashes = useMemo(
    () => sides.map((p) => p.position_hash).join(","),
    [sides],
  );

  const gateOk = useAccess(SCOPES.pricing).ok;

  const historyQ = usePriceHistory(contestId, {
    position_hash: joinedHashes || undefined,
    windowMs: range.ms,
    resolution: range.resolution,
    limit: POINT_LIMIT,
  });

  // Group points by position_hash → one best-price line per side.
  const series = useMemo<Series[]>(() => {
    const pts = historyQ.data?.points ?? [];
    const byHash = new Map<string, Map<number, number>>(); // hash → t → best(min) price
    for (const pt of pts) {
      if (typeof pt.price !== "number" || typeof pt.timestamp !== "number") continue;
      const h = pt.position_hash;
      if (!h) continue;
      const m = byHash.get(h) ?? new Map<number, number>();
      const cur = m.get(pt.timestamp);
      // best available for a side = the lowest venue price at that moment
      if (cur == null || pt.price < cur) m.set(pt.timestamp, pt.price);
      byHash.set(h, m);
    }
    return sides
      .map((p, i) => {
        const m = byHash.get(p.position_hash);
        const points = m
          ? [...m.entries()].sort((a, b) => a[0] - b[0]).map(([t, price]) => ({ t, price }))
          : [];
        return {
          hash: p.position_hash,
          label: sideLabel(p, sides),
          color: i < SIDE_COLORS.length ? SIDE_COLORS[i] : SIDE_FOLD,
          points,
        };
      })
      .filter((s) => s.points.length > 0);
  }, [historyQ.data, sides]);

  if (positions.length === 0) return null;

  return (
    <section className={flush ? "bg-panel" : "mx-2 mb-4 rounded border border-border-2 bg-panel"}>
      <div className="flex flex-wrap items-center gap-2 border-b border-border-2 px-3 py-2 lg:py-1.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-amber">price history</h3>
        <span className="text-[11px] text-faint lg:text-[10px]">best available · each side</span>
        <div className="ml-auto flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={`nums rounded border px-2.5 py-1.5 text-xs transition lg:px-1.5 lg:py-0.5 lg:text-[10px] ${
                r.key === rangeKey
                  ? "border-blue/60 bg-blue/15 text-blue"
                  : "border-border-2 text-dim hover:text-ink"
              }`}
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>

      {!gateOk ? (
        <LockedInline scope={SCOPES.pricing} />
      ) : historyQ.isLoading ? (
        <Loading label="loading history…" />
      ) : historyQ.isError ? (
        <ErrorState error={historyQ.error} onRetry={() => historyQ.refetch()} />
      ) : series.length === 0 ? (
        <EmptyState title="No price history in this window." hint="Try a wider range." />
      ) : (
        <LineChart series={series} tall={flush} />
      )}
    </section>
  );
}

/* ── side ordering + labels ── */

const SIDE_RANK: Record<string, number> = { home: 0, over: 0, yes: 0, away: 1, under: 1, no: 1 };

function orderSides(positions: LiquidityPosition[]): LiquidityPosition[] {
  return [...positions].sort((a, b) => {
    const ra = SIDE_RANK[(a.side_key ?? "").toLowerCase()] ?? 9;
    const rb = SIDE_RANK[(b.side_key ?? "").toLowerCase()] ?? 9;
    return ra - rb;
  });
}

/** Label a side by its selection: participant when multi, else the side key. */
function sideLabel(p: LiquidityPosition, all: LiquidityPosition[]): string {
  const names = new Set(all.map((x) => x.participant_name ?? ""));
  if (names.size > 1 && p.participant_name) return p.participant_name;
  if (p.side_key) return p.side_key.toUpperCase();
  return p.participant_name ?? p.title ?? "side";
}

/* ── chart ── */

interface Series {
  hash: string;
  label: string;
  color: string;
  points: { t: number; price: number }[];
}

// Desktop viewBox geometry — unchanged. Mobile uses fewer viewBox units across the same
// CSS width, so the (viewBox-unit) font sizes render large enough to read at 375px.
const DESKTOP_W = 1000;
const DESKTOP_PAD = { l: 42, r: 64, t: 12, b: 22 }; // right pad holds direct labels
const MOBILE_W = 420;
const MOBILE_PAD = { l: 36, r: 54, t: 12, b: 20 };

function LineChart({ series, tall = false }: { series: Series[]; tall?: boolean }) {
  const isMobile = useIsMobile();
  const W = isMobile ? MOBILE_W : DESKTOP_W;
  const PAD = isMobile ? MOBILE_PAD : DESKTOP_PAD;
  const fsTick = isMobile ? 10 : 9;
  const fsLabel = isMobile ? 11 : 10;
  const fsSub = isMobile ? 9 : 8;
  const H = tall ? 300 : 180;
  const svgRef = useRef<SVGSVGElement>(null);
  const [cursor, setCursor] = useState<number | null>(null);

  const { t0, t1, p0, p1 } = useMemo(() => {
    let t0 = Infinity, t1 = -Infinity, p0 = Infinity, p1 = -Infinity;
    for (const s of series)
      for (const pt of s.points) {
        if (pt.t < t0) t0 = pt.t;
        if (pt.t > t1) t1 = pt.t;
        if (pt.price < p0) p0 = pt.price;
        if (pt.price > p1) p1 = pt.price;
      }
    const span = p1 - p0;
    const pad = span > 0 ? span * 0.12 : 0.02;
    return {
      t0,
      t1: t1 > t0 ? t1 : t0 + 1,
      p0: Math.max(0, p0 - pad),
      p1: Math.min(1, p1 + pad),
    };
  }, [series]);

  const x = (t: number) => PAD.l + ((t - t0) / (t1 - t0)) * (W - PAD.l - PAD.r);
  const y = (p: number) => PAD.t + (1 - (p - p0) / (p1 - p0 || 1)) * (H - PAD.t - PAD.b);

  const yTicks = useMemo(
    () => Array.from({ length: 4 }, (_, i) => p0 + ((p1 - p0) * i) / 3),
    [p0, p1],
  );
  const xTicks = useMemo(
    () => Array.from({ length: 4 }, (_, i) => t0 + ((t1 - t0) * i) / 3),
    [t0, t1],
  );

  const hover = useMemo(() => {
    if (cursor == null) return null;
    const rows = series
      .map((s) => {
        let best: Series["points"][number] | null = null;
        for (const pt of s.points)
          if (best === null || Math.abs(pt.t - cursor) < Math.abs(best.t - cursor)) best = pt;
        return best ? { label: s.label, color: s.color, ...best } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
    return rows.length ? { t: rows[0].t, rows } : null;
  }, [cursor, series]);

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const fx = ((e.clientX - rect.left) / rect.width) * W;
    const t = t0 + ((fx - PAD.l) / (W - PAD.l - PAD.r)) * (t1 - t0);
    setCursor(Math.min(t1, Math.max(t0, t)));
  };

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full"
        style={{ shapeRendering: "geometricPrecision" }}
        onMouseMove={onMove}
        onMouseLeave={() => setCursor(null)}
        role="img"
        aria-label="price history, one line per side"
      >
        {/* recessive grid + cent labels */}
        {yTicks.map((p, i) => (
          <g key={i}>
            <line
              x1={PAD.l}
              x2={W - PAD.r}
              y1={y(p)}
              y2={y(p)}
              stroke="currentColor"
              strokeOpacity={0.07}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={PAD.l - 8}
              y={y(p) + 3}
              textAnchor="end"
              fontSize={fsTick}
              fill="currentColor"
              fillOpacity={0.4}
              className="nums"
            >
              {fmtCents(p)}
            </text>
          </g>
        ))}
        {/* time axis */}
        {xTicks.map((t, i) => (
          <text
            key={i}
            x={x(t)}
            y={H - 7}
            textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}
            fontSize={fsTick}
            fill="currentColor"
            fillOpacity={0.4}
            className="nums"
          >
            {new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </text>
        ))}

        {/* sharp stepped lines + faint area fill per side */}
        <defs>
          {series.map((s, i) => (
            <linearGradient key={i} id={`fill-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.16} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        {series.map((s, i) => (
          <g key={s.hash}>
            <path d={areaPath(s.points, x, y, H - PAD.b)} fill={`url(#fill-${i})`} stroke="none" />
            <path
              d={stepPath(s.points, x, y)}
              fill="none"
              stroke={s.color}
              strokeWidth={1.75}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ))}

        {/* direct labels at each line's right end */}
        {series.map((s) => {
          const last = s.points[s.points.length - 1];
          if (!last) return null;
          return (
            <g key={`lbl-${s.hash}`}>
              <circle cx={x(last.t)} cy={y(last.price)} r={2.5} fill={s.color} />
              <text
                x={W - PAD.r + 6}
                y={y(last.price) + 3}
                fontSize={fsLabel}
                fill={s.color}
                className="nums"
                style={{ fontWeight: 600 }}
              >
                {fmtCents(last.price)}
              </text>
              {/* HTML label so a long side name WRAPS in the gutter instead of
                  clipping at the SVG edge (SVG <text> can't wrap). */}
              <foreignObject
                x={W - PAD.r + 6}
                y={y(last.price) + 7}
                width={PAD.r - 8}
                height={44}
                style={{ overflow: "visible" }}
              >
                <div
                  style={{
                    fontSize: fsSub,
                    lineHeight: 1.1,
                    opacity: 0.5,
                    color: "currentColor",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  {s.label}
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* crosshair */}
        {hover && (
          <g>
            <line
              x1={x(hover.t)}
              x2={x(hover.t)}
              y1={PAD.t}
              y2={H - PAD.b}
              stroke="currentColor"
              strokeOpacity={0.28}
              strokeWidth={1}
              strokeDasharray="2 3"
              vectorEffect="non-scaling-stroke"
            />
            {hover.rows.map((r) => (
              <circle
                key={r.label}
                cx={x(r.t)}
                cy={y(r.price)}
                r={3.5}
                fill={r.color}
                stroke="var(--color-panel)"
                strokeWidth={1.5}
              />
            ))}
          </g>
        )}
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute top-1 z-10 min-w-[9rem] rounded border border-border-2 bg-panel/95 px-2 py-1.5 shadow-lg backdrop-blur"
          style={{ left: `${Math.min(70, Math.max(2, (x(hover.t) / W) * 100 + 2))}%` }}
        >
          <div className="nums mb-1 text-[10px] text-faint">
            {new Date(hover.t).toLocaleTimeString()}
          </div>
          {hover.rows
            .sort((a, b) => b.price - a.price)
            .map((r) => (
              <div key={r.label} className="flex items-center gap-1.5 py-0.5 text-[11px]">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ background: r.color }}
                />
                <span className="min-w-0 break-words text-dim">{r.label}</span>
                <span className="nums ml-auto text-ink">{fmtCents(r.price)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/** Step-after path: a quote holds until the next tick replaces it. */
function stepPath(
  pts: { t: number; price: number }[],
  x: (t: number) => number,
  y: (p: number) => number,
): string {
  if (pts.length === 0) return "";
  let d = `M ${x(pts[0].t).toFixed(1)} ${y(pts[0].price).toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    d += ` H ${x(pts[i].t).toFixed(1)} V ${y(pts[i].price).toFixed(1)}`;
  }
  return d;
}

/** Same step path, closed to the baseline for a faint area fill. */
function areaPath(
  pts: { t: number; price: number }[],
  x: (t: number) => number,
  y: (p: number) => number,
  baseY: number,
): string {
  if (pts.length === 0) return "";
  const line = stepPath(pts, x, y);
  const lastX = x(pts[pts.length - 1].t).toFixed(1);
  const firstX = x(pts[0].t).toFixed(1);
  return `${line} L ${lastX} ${baseY.toFixed(1)} L ${firstX} ${baseY.toFixed(1)} Z`;
}
